import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { WithdrawalService } from '../../../_core/_services/withdrawal.service';
import { WithdrawalDto } from '../../../_core/_model/withdrawal';
import { ApiService } from '../../../_core/_services/api.service';

interface WithdrawalTimelineItem {
  label: string;
  date?: string | null;
  description: string;
  status: 'done' | 'active' | 'pending' | 'failed';
}

@Component({
  selector: 'app-withdrawal-details-modal',
  standalone: false,
  templateUrl: './withdrawal-details-modal.html',
  styleUrl: './withdrawal-details-modal.css'
})
export class WithdrawalDetailsModal implements OnChanges {
  @Input() visible = false;
  @Input() withdrawal: WithdrawalDto | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();

  loadingReceipt = false;

  loadingDetails = false;
  showInteracPin = false;
  pinCopied = false;

  private detailRequestId = 0;
  private pinCopiedTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private api: ApiService,
    private withdrawalService: WithdrawalService
  ) { }

  close(): void {
    this.detailRequestId++;
    this.loadingDetails = false;
    this.showInteracPin = false;
    this.pinCopied = false;

    if (this.pinCopiedTimer) {
      clearTimeout(this.pinCopiedTimer);
      this.pinCopiedTimer = undefined;
    }

    this.visible = false;
    this.visibleChange.emit(false);
  }

  get hasInteracInstruction(): boolean {
    const instruction = this.withdrawal?.interacInstruction;
    const status = (this.withdrawal?.status || '').toLowerCase();

    return (
      ['completed', 'confirmed'].includes(status) &&
      !!instruction?.pin
    );
  }

  get maskedInteracPin(): string {
    const pin = this.withdrawal?.interacInstruction?.pin || '';
    const length = Math.max(pin.length, 6);

    return '•'.repeat(length);
  }

  toggleInteracPin(): void {
    this.showInteracPin = !this.showInteracPin;
  }

  async copyInteracPin(): Promise<void> {
    const pin = this.withdrawal?.interacInstruction?.pin;

    if (!pin)
      return;

    try {
      await navigator.clipboard.writeText(pin);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = pin;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';

      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }

    this.pinCopied = true;

    if (this.pinCopiedTimer) {
      clearTimeout(this.pinCopiedTimer);
    }

    this.pinCopiedTimer = setTimeout(() => {
      this.pinCopied = false;
    }, 2000);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const modalOpened =
      changes['visible']?.currentValue === true;

    const withdrawalChanged =
      !!changes['withdrawal'] &&
      changes['withdrawal'].currentValue?.withdrawalId !==
      changes['withdrawal'].previousValue?.withdrawalId;

    if (
      this.visible &&
      this.withdrawal?.withdrawalId &&
      (modalOpened || withdrawalChanged)
    ) {
      this.loadWithdrawalDetails(this.withdrawal.withdrawalId);
    }
  }

  private loadWithdrawalDetails(withdrawalId: string): void {
    const requestId = ++this.detailRequestId;

    this.loadingDetails = true;
    this.showInteracPin = false;
    this.pinCopied = false;

    this.withdrawalService.getWithdrawalById(withdrawalId).subscribe({
      next: (response) => {
        if (requestId !== this.detailRequestId)
          return;

        if (response.data) {
          this.withdrawal = response.data;
        }
      },
      error: () => {
        if (requestId === this.detailRequestId) {
          this.loadingDetails = false;
        }
      },
      complete: () => {
        if (requestId === this.detailRequestId) {
          this.loadingDetails = false;
        }
      }
    });
  }

  backdropClose(event: MouseEvent): void {
    if ((event.target as HTMLElement)?.classList.contains('modal')) {
      this.close();
    }
  }

  get canShowReceiptActions(): boolean {
    if (!this.withdrawal) return false;

    const status = (this.withdrawal.status || '').toLowerCase();
    return !!this.withdrawal.receiptNumber && ['completed', 'confirmed'].includes(status);
  }

  viewReceipt(): void {
    if (!this.withdrawal) return;

    this.loadingReceipt = true;

    this.api.getWithdrawalReceipt(this.withdrawal.withdrawalId).subscribe({
      next: (response) => {
        const blob = response.body;
        if (!blob) {
          this.loadingReceipt = false;
          return;
        }

        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener');

        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 10000);

        this.loadingReceipt = false;
      },
      error: () => {
        this.loadingReceipt = false;
      }
    });
  }

  downloadReceipt(): void {
    if (!this.withdrawal) return;

    this.loadingReceipt = true;

    this.api.getWithdrawalReceipt(this.withdrawal.withdrawalId).subscribe({
      next: (response) => {
        const blob = response.body;
        if (!blob) {
          this.loadingReceipt = false;
          return;
        }

        const contentDisposition = response.headers.get('content-disposition');
        const match = contentDisposition?.match(/filename="?([^"]+)"?/i);
        const fileName = match?.[1] || `withdrawal-receipt-${this.withdrawal?.receiptNumber || this.withdrawal?.withdrawalId}.pdf`;

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();

        window.URL.revokeObjectURL(url);
        this.loadingReceipt = false;
      },
      error: () => {
        this.loadingReceipt = false;
      }
    });
  }

  get timeline(): WithdrawalTimelineItem[] {
    if (!this.withdrawal) return [];

    const status = (this.withdrawal.status || '').toString().toLowerCase();

    const createdDone = true;
    const processingDone = !!this.withdrawal.submittedAt || ['processing', 'completed', 'failed', 'confirmed'].includes(status);
    const completedDone = !!this.withdrawal.completedAt || ['completed', 'confirmed'].includes(status);
    const failedDone = !!this.withdrawal.failedAt || status === 'failed';

    return [
      {
        label: 'Created',
        date: this.withdrawal.createdAt,
        description: 'Withdrawal request was created.',
        status: createdDone ? 'done' : 'pending'
      },
      {
        label: 'Processing',
        date: this.withdrawal.submittedAt || null,
        description: 'Transfer was submitted to the payment gateway.',
        status: completedDone || processingDone
          ? 'done'
          : failedDone
            ? 'failed'
            : 'pending'
      },
      {
        label: 'Completed',
        date: this.withdrawal.completedAt || null,
        description: 'Withdrawal was completed successfully.',
        status: completedDone
          ? 'done'
          : status === 'processing'
            ? 'active'
            : 'pending'
      },
      {
        label: 'Failed',
        date: this.withdrawal.failedAt || null,
        description: this.withdrawal.failureReason || 'Withdrawal failed.',
        status: failedDone ? 'failed' : 'pending'
      }
    ];
  }

  badgeClass(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'completed':
      case 'confirmed':
        return 'bg-success-subtle text-success';
      case 'failed':
      case 'cancelled':
        return 'bg-danger-subtle text-danger';
      case 'processing':
      case 'pending':
        return 'bg-warning-subtle text-warning';
      default:
        return 'bg-secondary-subtle text-secondary';
    }
  }

  timelineDotClass(status: 'done' | 'active' | 'pending' | 'failed'): string {
    switch (status) {
      case 'done':
        return 'timeline-dot timeline-dot-done';
      case 'active':
        return 'timeline-dot timeline-dot-active';
      case 'failed':
        return 'timeline-dot timeline-dot-failed';
      default:
        return 'timeline-dot timeline-dot-pending';
    }
  }

  get totalDuration(): string {
    if (!this.withdrawal) return '-';

    const start = new Date(this.withdrawal.createdAt).getTime();
    const end = this.withdrawal.completedAt
      ? new Date(this.withdrawal.completedAt).getTime()
      : this.withdrawal.failedAt
        ? new Date(this.withdrawal.failedAt).getTime()
        : this.withdrawal.submittedAt
          ? new Date(this.withdrawal.submittedAt).getTime()
          : new Date(this.withdrawal.lastUpdatedAt).getTime();

    return this.formatDuration(end - start);
  }

  stageDuration(from?: string | null, to?: string | null): string {
    if (!from || !to) return '-';

    const start = new Date(from).getTime();
    const end = new Date(to).getTime();

    return this.formatDuration(end - start);
  }

  private formatDuration(ms: number): string {
    if (!ms || ms < 0) return '-';

    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  }
}